'use client';

import { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { RadioGroup, RadioGroupOption } from '../Inputs/RadioGroup';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

type ReasonValue = 'SoldHere' | 'SoldOnOtherPlatform' | 'Other';
type Props = {
  defaultValue?: ReasonValue;
  defaultText?: string;
  onSave: (reason: ReasonValue, text?: string) => void;
};

export function ArchiveReasonGroup({ defaultValue = 'SoldHere', defaultText = '', onSave }: Props) {
  const t = useTranslations('modals.stopping');

  const [value, setValue] = useState<ReasonValue>(defaultValue);
  const [text, setText] = useState(defaultText);

  const options = useMemo<RadioGroupOption<ReasonValue>[]>(
    () => [
      {
        value: 'SoldHere',
        label: t('sold_here'),
      },
      {
        value: 'SoldOnOtherPlatform',
        label: t('sold_somewhere'),
      },
      {
        value: 'Other',
        label: t('other'),
      },
    ],
    [t],
  );

  const handleChange = (v: ReasonValue) => {
    setValue(v);
  };

  const handleText = (t: string) => {
    setText(t);
  };

  const onClickSave = useCallback(() => {
    onSave(value, text || '');
  }, [onSave, value, text]);

  return (
    <div className='w-full'>
      <RadioGroup options={options} value={value} onValueChange={handleChange} withDividers />

      {/* conditional textarea */}
      <div className={clsx('overflow-hidden transition-all', value === 'Other' ? 'max-h-40 mt-3' : 'max-h-0')}>
        <Label className='block text-[13px] text-neutral-500 mb-1'>{t('your_reason')}</Label>
        <Textarea
          value={text}
          onChange={(e) => handleText(e.target.value)}
          placeholder={t('other_placeholder')}
          rows={3}
          className='resize-none rounded-[10px] border-neutral-200 px-3 py-2 text-[14px] placeholder:text-neutral-400 focus-visible:border-main-green focus-visible:ring-0'
        />

        {value === 'Other' && text.trim().length === 0 && (
          <p className='text-[14px] leading-[20px] text-red-day error-form'>{t('other_empty')}</p>
        )}
      </div>

      <Button
        variant='default'
        onClick={onClickSave}
        className='w-full mt-[16px] h-auto py-[10px] rounded-[12px] text-bg-day text-s'
      >
        {t('button')}
      </Button>
    </div>
  );
}
